import type { ReactNode } from 'react';
import { Surface } from './Surface';

// ------------------------------------------------------------------
// TaskCard — one unit of work, on a phone (story 5-2b; P10 §2, D44).
// ------------------------------------------------------------------
//
// THE GAP THIS FILLS. Every list surface in the product so far has been a
// TABLE (`EntityListPage` → `Table`), and a table is the right answer for a
// desk: many columns, scanned by eye, sorted by header. P05 §8 asks for the
// opposite shape — "the 390px path has ≥48px targets and routes Start/Resume
// directly to capture" — a stack of tappable cards, each of which is one job,
// with its own primary action under a thumb. There was no primitive for that,
// and the alternative was a one-off `<div className="rounded-xl border …">`
// inside the scheduling module, which D44 rejects outright.
//
// ── What it guarantees, and why each one is a guarantee and not a prop ──
//
// 1. **The whole card is ONE control.** `onOpen` renders the card as a
//    `<button>`, so it is reachable by Tab, activated by Enter/Space and
//    announced once — rather than a `<div onClick>` with a nested link a
//    screen-reader user meets twice. The primary ACTION is a separate button
//    that stops propagation, because "open this job" and "start this job" are
//    two different intents and a phone user hits whichever is under the thumb.
//
// 2. **≥48px, structurally.** `min-h-touch`
//    (story 2-137's touch-target role, aliased to the hard 48px floor) on
//    the action and on the card's own hit area, not "the padding usually
//    adds up to enough". P05 §8 makes the number a requirement, so it lives
//    in the primitive where one change fixes every consumer — and where the
//    e2e gate can measure it.
//
// 3. **The accent is a STATE, not a decoration.** `accent` paints the left bar
//    and nothing else; the copy stays `--foreground` / `--muted-foreground`,
//    the two roles `scripts/contrast-check.mjs` verifies at AA in both themes.
//    Same rule, and the same reason, as `Callout`: `--warning` is a
//    96%-saturation amber that does not reach 4.5:1 against `--background` in
//    light mode, so a primitive that rendered prose in it would ship an
//    unreadable sentence the token check does not cover. It is also why the
//    accent is never the ONLY carrier of meaning — a card wearing a red bar
//    always also wears a `Badge` that says "Overdue" in words (WCAG 1.4.1).
//
// 4. **Warnings are announced.** `notice` mounts as `role="status"`: a card
//    that grows an explanation while somebody is looking at the list (a job
//    reassigned away under them — P05 §8's live-change clause) must SAY so,
//    not merely redraw.
//
// PROMOTION CANDIDATES (P18 §6, rule of three). The workload board's column
// cards (story 5-3) and `ActionCard` (P05 §3, story 5-6) are the same shape:
// a titled unit of work with a status accent, a meta row and one primary verb.
// This is written as the general primitive rather than as `MyDayCard` so those
// two compose it instead of growing a third variant.

export type TaskCardAccent = 'neutral' | 'primary' | 'success' | 'warning' | 'destructive';

const ACCENT_VAR: Record<TaskCardAccent, string> = {
  neutral: '--border',
  primary: '--primary',
  success: '--success',
  warning: '--warning',
  destructive: '--destructive',
};

export interface TaskCardProps {
  /** The thing this card is about — a site, a subject, a job. Required. */
  title: string;
  /** One line under the title: what KIND of work it is. */
  subtitle?: string;
  /**
   * Short facts, rendered as a wrapping row: time, due state, status.
   * Each is a node so a consumer can pass a `Badge` — the primitive does not
   * decide what a status LOOKS like, only where it sits.
   */
  meta?: ReactNode;
  /** Drives the left bar only — never the text colour (see above). */
  accent?: TaskCardAccent;
  /**
   * An explanation the card grew: a warning, or the sentence saying the work
   * changed hands. Announced politely on insertion.
   */
  notice?: ReactNode;
  /** The primary verb, under the thumb. Compose a DS `Button` here. */
  action?: ReactNode;
  /**
   * Makes the whole card the control that opens the record. Omitted, the card
   * is a plain container — a card that is not openable must not LOOK openable.
   */
  onOpen?: () => void;
  /** Announced name for the whole-card control. Defaults to `Open <title>`. */
  openLabel?: string;
  className?: string;
  'data-testid'?: string;
}

export function TaskCard({
  title,
  subtitle,
  meta,
  accent = 'neutral',
  notice,
  action,
  onOpen,
  openLabel,
  className = '',
  'data-testid': testId,
}: TaskCardProps): JSX.Element {
  const body = (
    <>
      <p className="text-base font-semibold leading-tight text-foreground">{title}</p>
      {subtitle && <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>}
      {meta && <div className="mt-2 flex flex-wrap items-center gap-2">{meta}</div>}
    </>
  );

  return (
    // Story 2-147ta: the `rounded-hairline border border-border bg-card`
    // visual treatment now comes from `Surface` (flat, no padding — this
    // card owns its own asymmetric `pl-5`/`pr-3`/`py-3` rhythm below,
    // because of the accent bar) instead of being re-derived locally.
    // `data-accent` is real per-card state (read by this file's own e2e/CSS
    // consumers) — `Surface`'s own `data-*` escape hatch carries it through
    // rather than this card reaching past `Surface` for a raw element.
    <Surface
      as="article"
      variant="flat"
      padding="none"
      {...(testId ? { 'data-testid': testId } : {})}
      data-accent={accent}
      className={`relative overflow-hidden ${className}`}
    >
      {/* Decorative: the STATE it encodes is always also written in the meta
          row as words, so nothing depends on seeing this bar. */}
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-1.5"
        style={{ backgroundColor: `hsl(var(${ACCENT_VAR[accent]}))` }}
      />
      <div className="flex flex-col gap-3 py-3 pl-5 pr-3 sm:flex-row sm:items-center sm:justify-between">
        {onOpen ? (
          <button
            type="button"
            onClick={onOpen}
            aria-label={openLabel ?? `Open ${title}`}
            className="focus-ring -m-1 flex min-h-touch min-w-0 flex-1 flex-col justify-center rounded-lg p-1 text-left"
          >
            {body}
          </button>
        ) : (
          <div className="flex min-h-touch min-w-0 flex-1 flex-col justify-center">
            {body}
          </div>
        )}
        {/* The ≥48px rule from this file's header note 2, actually enforced.
            It is applied to the slot's CHILDREN rather than to the wrapper
            because the wrapper's height is not what a thumb hits and not what
            the gate measures — the control is. Stated once here so every
            consumer inherits it and no call site has to remember. */}
        {action && (
          <div className="flex shrink-0 items-center gap-2 sm:pl-3 [&>*]:min-h-touch">
            {action}
          </div>
        )}
      </div>
      {notice && (
        <div role="status" className="border-t border-border/60 px-5 py-2 text-sm text-muted-foreground">
          {notice}
        </div>
      )}
    </Surface>
  );
}
